import type { ImageType } from '@vizit/api';
import * as React from 'react';
import { CollectionGridContext } from './collection-grid-context';
import type { CollectionSortOption } from './collection-grid-provider.state';
import { CollectionGridFilterOptions } from './collection-grid-provider.state';
import { DEFAULT_FILTER_STATE } from './collection-grid-provider.state';

interface CollectionGridProviderProps extends React.PropsWithChildren {
  /**
   * The ID of the portfolio to fetch collections for
   *
   * @optional
   * @type {string}
   */
  workspaceId?: string;
  /**
   * The ID of the organization
   *
   * @optional
   * @type {string}
   */
  organizationId?: string;
  /**
   * Number of items to fetch per page
   *
   * @optional
   * @type {number}
   * @default 12
   */
  limit?: number;
  productCategoryId?: string | null;
  productCategoryName?: string;
  imageType?: ImageType | null;
  searchTerm?: string;
  /** @default CollectionSortOption.DateAdded */
  sortOption?: CollectionSortOption;
  onCollectionGridFilterChange?: (filter: CollectionGridFilterOptions, value?: string) => void;
}

/**
 * Provider component for the collection grid context.
 * Manages the state for collection grid pagination.
 *
 * @param props - The provider props
 */
function CollectionGridProvider(props: CollectionGridProviderProps) {
  const {
    children,
    workspaceId,
    organizationId,
    limit = 12,
    productCategoryId,
    productCategoryName,
    imageType,
    searchTerm,
    sortOption,
    onCollectionGridFilterChange,
  } = props;

  const handleOnFilterProductCategory = React.useCallback(
    (nextProductCategoryId: string | null, nextProductCategoryName: string) => {
      onCollectionGridFilterChange?.(CollectionGridFilterOptions.ProductCategory, nextProductCategoryId || undefined);
      onCollectionGridFilterChange?.(
        CollectionGridFilterOptions.ProductCategoryName,
        nextProductCategoryName || undefined,
      );
    },
    [onCollectionGridFilterChange],
  );

  const handleOnFilterImageType = React.useCallback(
    (nextImageType: ImageType | null) => {
      onCollectionGridFilterChange?.(CollectionGridFilterOptions.ImageType, nextImageType || undefined);
    },
    [onCollectionGridFilterChange],
  );

  const handleOnSearchTermChange = React.useCallback(
    (nextSearchTerm: string) => {
      const trimmedSearchTerm = nextSearchTerm.trim();
      onCollectionGridFilterChange?.(CollectionGridFilterOptions.Search, trimmedSearchTerm || undefined);
    },
    [onCollectionGridFilterChange],
  );

  const handleOnSortOptionChange = React.useCallback(
    (nextSortOption: CollectionSortOption) => {
      onCollectionGridFilterChange?.(CollectionGridFilterOptions.Sort, nextSortOption);
    },
    [onCollectionGridFilterChange],
  );

  const value = React.useMemo(
    () => ({
      workspaceId,
      organizationId,
      limit,
      productCategoryId: productCategoryId ?? DEFAULT_FILTER_STATE.productCategoryId,
      productCategoryName: productCategoryName ?? DEFAULT_FILTER_STATE.productCategoryName,
      imageType: imageType ?? DEFAULT_FILTER_STATE.imageType,
      searchTerm: searchTerm ?? DEFAULT_FILTER_STATE.searchTerm,
      sortOption: sortOption ?? DEFAULT_FILTER_STATE.sortOption,
      onFilterProductCategory: handleOnFilterProductCategory,
      onFilterImageType: handleOnFilterImageType,
      onSearchTermChange: handleOnSearchTermChange,
      onSortOptionChange: handleOnSortOptionChange,
    }),
    [
      workspaceId,
      organizationId,
      limit,
      productCategoryId,
      productCategoryName,
      imageType,
      searchTerm,
      sortOption,
      handleOnFilterProductCategory,
      handleOnFilterImageType,
      handleOnSearchTermChange,
      handleOnSortOptionChange,
    ],
  );

  return <CollectionGridContext.Provider value={value}>{children}</CollectionGridContext.Provider>;
}

/**
 * Hook to access the collection grid context.
 * Must be used within a CollectionGridProvider.
 *
 * @throws Error if used outside of CollectionGridProvider
 * @returns The collection grid context
 */
const useCollectionGrid = () => {
  const context = React.useContext(CollectionGridContext);

  if (!context) {
    throw new Error('useCollectionGrid must be used within a CollectionGridProvider');
  }

  return context;
};

export { CollectionGridProvider, useCollectionGrid };
